import React from "react";

import type { LucideIcon } from "lucide-react";
import { ArrowRight } from "lucide-react";

type SynopsisSectionCardProps = {
  icon: LucideIcon;
  title: string;
  linkLabel?: string;
  onLinkClick?: () => void;
  /** Optional muted text shown before the header link (e.g. date range). */
  headerMeta?: string;
  /** Extra control next to the title (e.g. info tooltip). */
  titleInfo?: React.ReactNode;
  /** Extra header controls (e.g. status chip, action link), before meta/link. */
  headerExtra?: React.ReactNode;
  /** Icon badge tint — defaults to teal. */
  iconTone?: "teal" | "orange" | "blue" | "brown";
  hideHeader?: boolean;
  children: React.ReactNode;
};

const ICON_TONE_CLASS: Record<
  NonNullable<SynopsisSectionCardProps["iconTone"]>,
  string
> = {
  teal: "bg-teal-600 text-white",
  orange: "bg-orange-500 text-white",
  blue: "bg-blue-600 text-white",
  brown: "bg-[#b06c45] text-white",
};

const SynopsisSectionCard: React.FC<SynopsisSectionCardProps> = ({
  icon: Icon,
  title,
  linkLabel,
  onLinkClick,
  headerMeta,
  headerExtra,
  titleInfo,
  iconTone = "teal",
  hideHeader = false,
  children,
}) => (
  <section className="w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
    {hideHeader ? null : (
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3.5">
        <span className="flex min-w-0 items-center gap-2.5">
          <span
            className={`inline-flex size-7 shrink-0 items-center justify-center rounded-md ${ICON_TONE_CLASS[iconTone]}`}
          >
            <Icon size={15} strokeWidth={2.25} aria-hidden />
          </span>
          <span className="flex min-w-0 items-center gap-1">
            <span className="truncate text-sm font-semibold leading-none text-gray-900">
              {title}
            </span>
            {titleInfo}
          </span>
        </span>
        <div className="flex min-w-0 flex-wrap items-center justify-end gap-x-3 gap-y-1">
          {headerExtra}
          {headerMeta ? (
            <span className="text-xs leading-none text-gray-400">
              {headerMeta}
            </span>
          ) : null}
          {linkLabel && onLinkClick ? (
            <button
              type="button"
              className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap border-0 bg-transparent p-0 text-xs font-medium leading-none text-[#7C3AED] hover:underline"
              onClick={onLinkClick}
            >
              <ArrowRight
                size={12}
                strokeWidth={2.5}
                className="shrink-0"
                aria-hidden
              />
              <span>{linkLabel}</span>
            </button>
          ) : null}
        </div>
      </div>
    )}
    <div className="px-4 py-5">{children}</div>
  </section>
);

export default SynopsisSectionCard;
